import { useEffect, useRef, useState } from "react";
import AddressAutocomplete from "../AddressAutocomplete";
import { apiUrl, getTenantHeaders } from "../api";

const TERMS_OPTIONS = ["COD", "NET15", "NET30", "NET45", "NET60", "DUEONRECEIPT"];

const SELECT_STYLE = {
  padding: "10px 6px",
  border: "3px solid #ffffff",
  borderRadius: 12,
  fontWeight: 900,
  boxSizing: "border-box",
  background: "white",
};

const TimeDropdown = ({ value, onChange }) => {
  const hhmm = String(value || "09:00").slice(0, 5);
  const rawH = parseInt(hhmm.slice(0, 2), 10) || 0;
  const rawM = parseInt(hhmm.slice(3, 5), 10) || 0;
  const ampm = rawH >= 12 ? "PM" : "AM";
  const h12 = rawH % 12 === 0 ? 12 : rawH % 12;
  const MINS = [0, 15, 30, 45];
  const snapMin = MINS.reduce((prev, curr) =>
    Math.abs(curr - rawM) < Math.abs(prev - rawM) ? curr : prev
  );
  const rebuild = (h, m, ap) => {
    let h24 = Number(h) % 12;
    if (ap === "PM") h24 += 12;
    return `${String(h24).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
  };
  return (
    <div style={{ display: "flex", gap: 6 }}>
      <select style={{ ...SELECT_STYLE, flex: 1 }}
        value={h12}
        onChange={(e) => onChange(rebuild(e.target.value, snapMin, ampm))}>
        {[1,2,3,4,5,6,7,8,9,10,11,12].map((h) => (
          <option key={h} value={h}>{h}</option>
        ))}
      </select>
      <select style={{ ...SELECT_STYLE, flex: 1 }}
        value={snapMin}
        onChange={(e) => onChange(rebuild(h12, e.target.value, ampm))}>
        {MINS.map((m) => (
          <option key={m} value={m}>{String(m).padStart(2, "0")}</option>
        ))}
      </select>
      <select style={{ ...SELECT_STYLE, flex: 1 }}
        value={ampm}
        onChange={(e) => onChange(rebuild(h12, snapMin, e.target.value))}>
        <option value="AM">AM</option>
        <option value="PM">PM</option>
      </select>
    </div>
  );
};

const SHARE_MESSAGE = "Please review your appointment schedule below. To print or save a copy, click the button below. You can also revisit this link to see your latest appointment schedule.";

export const AppointmentShareControls = ({ appointmentId, customerMobile, customerEmail }) => {
  const [open, setOpen] = useState(false);
  const [publicUrl, setPublicUrl] = useState("");
  const [loadingAction, setLoadingAction] = useState("");
  const [message, setMessage] = useState("");

  if (!appointmentId) return null;

  const readLink = async () => {
    const response = await fetch(apiUrl(`/api/appointments/${encodeURIComponent(String(appointmentId))}/schedule-link`), {
      method: "POST",
      headers: { "Content-Type": "application/json", ...getTenantHeaders() },
      body: JSON.stringify({}),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data.url) throw new Error(data.error || "Unable to create the schedule link.");
    return data.url;
  };

  const openShareModal = async () => {
    if (loadingAction) return;
    setOpen(true);
    setPublicUrl("");
    setMessage("");
    setLoadingAction("copy");
    try {
      const url = await readLink();
      if (!navigator.clipboard?.writeText) throw new Error("Clipboard access is unavailable on this device.");
      await navigator.clipboard.writeText(url);
      setPublicUrl(url);
    } catch (error) {
      setMessage(error?.message || "Unable to share the appointment schedule.");
    } finally {
      setLoadingAction("");
    }
  };

  const handleTextCustomer = () => {
    if (!publicUrl) return;
    const rawPhone = String(customerMobile || "").trim();
    const phone = rawPhone.startsWith("+")
      ? `+${rawPhone.slice(1).replace(/\D/g, "")}`
      : rawPhone.replace(/\D/g, "");
    if (phone.replace(/\D/g, "").length < 7) {
      setMessage("NO CUSTOMER PHONE AVAILABLE.");
      return;
    }
    setMessage("");
    window.location.href = `sms:${phone}?body=${encodeURIComponent(`${SHARE_MESSAGE}\n\n${publicUrl}`)}`;
  };

  const handleEmailCustomer = () => {
    if (!publicUrl) return;
    const email = String(customerEmail || "").trim();
    if (!email) {
      setMessage("NO CUSTOMER EMAIL AVAILABLE.");
      return;
    }
    setMessage("");
    const body = `${SHARE_MESSAGE}\r\n\r\n${publicUrl}`;
    window.location.href = `mailto:${email}?subject=${encodeURIComponent("Your Appointment Schedule")}&body=${encodeURIComponent(body)}`;
  };

  const closeShareModal = () => {
    setOpen(false);
    setPublicUrl("");
    setLoadingAction("");
    setMessage("");
  };

  const shareButtonStyle = {
    width: "100%",
    padding: "15px",
    marginBottom: "10px",
    fontWeight: 900,
    border: "4px solid black",
    cursor: "pointer",
    textTransform: "uppercase",
    fontSize: "16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
  };

  return (
    <div className="jf-appointment-share-controls" style={{ width: "100%" }}>
      <style>{`
        .jf-share-btn { transition: filter 120ms ease, transform 120ms ease; }
        .jf-share-btn:hover {
          background: #FFC107 !important;
          color: #000 !important;
          border-color: #FFA500 !important;
          filter: brightness(1.02);
        }
        .jf-share-btn:active { transform: translateY(1px); }
        @media (max-width: 1023px) {
          .jf-appointment-share-controls {
            margin-top: 10px !important;
          }
        }
      `}</style>
      <button
        type="button"
        className="jf-calendar-mobile-action"
        style={{
          width: "100%",
          minHeight: 44,
          padding: "10px 16px",
          border: "3px solid #000000",
          borderRadius: 12,
          background: "#FFC107",
          color: "#000000",
          fontWeight: 900,
          cursor: loadingAction ? "wait" : "pointer",
          boxSizing: "border-box",
        }}
        disabled={Boolean(loadingAction)}
        onMouseEnter={(event) => { event.currentTarget.style.background = "#FFD54F"; }}
        onMouseLeave={(event) => { event.currentTarget.style.background = "#FFC107"; }}
        onClick={openShareModal}
      >
        SHARE NOW
      </button>
      {open ? (
        <>
          <div
            style={{ position: "fixed", inset: 0, zIndex: 10050, backgroundColor: "rgba(0,0,0,0.65)" }}
            onMouseDown={(event) => event.stopPropagation()}
          />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby={publicUrl ? "appointment-share-title" : undefined}
            aria-label={publicUrl ? undefined : "Share appointment schedule"}
            style={{
              position: "fixed",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              zIndex: 10100,
              width: "calc(100% - 32px)",
              maxWidth: "440px",
              maxHeight: "calc(100vh - 32px)",
              overflowY: "auto",
              WebkitOverflowScrolling: "touch",
              boxSizing: "border-box",
              backgroundColor: "white",
              color: "black",
              border: "8px solid black",
              boxShadow: "0 0 0 6px #E8761D",
              padding: "16px",
            }}
            onMouseDown={(event) => event.stopPropagation()}
          >
            {loadingAction ? (
              <div role="status" style={{ padding: "12px", textAlign: "center", fontWeight: 900, fontSize: "16px" }}>
                GETTING LINK AND COPYING TO CLIPBOARD...
              </div>
            ) : null}
            {!loadingAction && message && !publicUrl ? (
              <>
                <div role="alert" style={{ padding: "12px", marginBottom: "10px", border: "3px solid black", backgroundColor: "#fee2e2", color: "black", fontWeight: 900, fontSize: "14px", textAlign: "center" }}>
                  {message}
                </div>
                <button type="button" className="jf-share-btn" style={{ ...shareButtonStyle, backgroundColor: "black", color: "white", marginBottom: 0 }} onClick={closeShareModal}>
                  CLOSE
                </button>
              </>
            ) : null}
            {publicUrl ? (
              <>
                <h3 id="appointment-share-title" style={{ margin: "0 0 8px", textAlign: "center", fontWeight: 900, fontSize: "22px" }}>
                  LINK READY TO SHARE!
                </h3>
                {message ? (
                  <div role="alert" style={{ padding: "10px", marginBottom: "10px", border: "3px solid black", backgroundColor: "#fee2e2", color: "black", fontWeight: 900, textAlign: "center" }}>
                    {message}
                  </div>
                ) : null}
                <button type="button" className="jf-share-btn" style={{ ...shareButtonStyle, backgroundColor: "#39FF14", marginBottom: "8px" }} onClick={handleTextCustomer}>
                  TEXT NOW
                </button>
                <button type="button" className="jf-share-btn" style={{ ...shareButtonStyle, backgroundColor: "#E8761D", marginBottom: "8px" }} onClick={handleEmailCustomer}>
                  EMAIL NOW
                </button>
                <button type="button" className="jf-share-btn" style={{ ...shareButtonStyle, backgroundColor: "black", color: "white", marginBottom: 0 }} onClick={closeShareModal}>
                  CLOSE NOW
                </button>
                <div style={{ marginTop: "10px", fontSize: "13px", fontWeight: 800, lineHeight: 1.45, textAlign: "center" }}>
                  <div>
                    Texting requires a mobile device<br />
                    or a configured SMS app.
                  </div>
                  <div style={{ marginTop: "8px" }}>
                    Your link is still on your clipboard<br />
                    and ready to paste anywhere.
                  </div>
                  <div style={{ marginTop: "8px" }}>
                    Paste it into any email or messaging app.
                  </div>
                </div>
              </>
            ) : null}
          </div>
        </>
      ) : null}
    </div>
  );
};

const AppointmentDeleteAction = ({ draft, deleteAppointment, deleteRecurringAppointment, deleteRecurringAppointments, recurringDeletionPending, showDeleteChoices, setShowDeleteChoices, styles }) => {
  const deleteChoicesRef = useRef(null);
  const isRecurring =
    draft?.recurrence_batch_id != null && draft?.recurrence_sequence != null;

  useEffect(() => {
    if (showDeleteChoices) {
      deleteChoicesRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [showDeleteChoices]);

  if (!isRecurring) {
    return (
      <button type="button" className="jf-calendar-mobile-action jf-calendar-mobile-delete" style={{ ...styles.dangerBtn, width: "100%", marginLeft: 0, boxSizing: "border-box" }}
        onMouseEnter={(e) => { e.currentTarget.style.background = "#FF0000"; e.currentTarget.style.color = "#FFFFFF"; e.currentTarget.style.borderColor = "#FFFFFF"; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = "#FF5252"; e.currentTarget.style.color = "#FFFFFF"; e.currentTarget.style.borderColor = "#FFFFFF"; }}
        onClick={deleteAppointment}>DELETE NOW</button>
    );
  }

  if (!showDeleteChoices) {
    return (
      <button type="button" className="jf-calendar-mobile-action jf-calendar-mobile-delete" style={{ ...styles.dangerBtn, width: "100%", marginLeft: 0, boxSizing: "border-box" }}
        onMouseEnter={(e) => { e.currentTarget.style.background = "#FF0000"; e.currentTarget.style.color = "#FFFFFF"; e.currentTarget.style.borderColor = "#FFFFFF"; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = "#FF5252"; e.currentTarget.style.color = "#FFFFFF"; e.currentTarget.style.borderColor = "#FFFFFF"; }}
        onClick={() => setShowDeleteChoices(true)}>DELETE NOW</button>
    );
  }

  return (
    <div ref={deleteChoicesRef} style={{ display: "flex", flexDirection: "column", alignItems: "stretch", gap: 10, width: "100%" }}>
      <button type="button" className="jf-calendar-mobile-action" style={{ ...styles.dangerBtn, width: "100%", boxSizing: "border-box", background: "#9A3412", color: "#E8E2DA", border: "2px solid #E8E2DA" }} disabled={recurringDeletionPending}
        onMouseEnter={(e) => { e.currentTarget.style.background = "#E8E2DA"; e.currentTarget.style.color = "#9A3412"; e.currentTarget.style.borderColor = "#9A3412"; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = "#9A3412"; e.currentTarget.style.color = "#E8E2DA"; e.currentTarget.style.borderColor = "#E8E2DA"; }}
        onClick={deleteRecurringAppointment}>
        DELETE THIS APPOINTMENT ONLY
      </button>
      <button type="button" className="jf-calendar-mobile-action jf-calendar-mobile-delete" style={{ ...styles.dangerBtn, width: "100%", boxSizing: "border-box" }} disabled={recurringDeletionPending}
        onMouseEnter={(e) => { e.currentTarget.style.background = "#FF0000"; e.currentTarget.style.color = "#FFFFFF"; e.currentTarget.style.borderColor = "#FFFFFF"; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = "#FF5252"; e.currentTarget.style.color = "#FFFFFF"; e.currentTarget.style.borderColor = "#FFFFFF"; }}
        onClick={deleteRecurringAppointments}>
        <span className="jf-recurring-delete-desktop-label">DELETE THIS &amp; ALL FUTURE APPOINTMENTS</span>
        <span className="jf-recurring-delete-mobile-label">DELETE THIS &amp; ALL FUTURE APPOINTMENTS</span>
      </button>
      <button type="button" className="jf-calendar-mobile-action" style={{ ...styles.cancelBtn, width: "100%", boxSizing: "border-box", background: "#374151", color: "#FFFFFF", border: "3px solid #FFFFFF" }} disabled={recurringDeletionPending}
        onMouseEnter={(e) => { e.currentTarget.style.background = "#FFFFFF"; e.currentTarget.style.color = "#374151"; e.currentTarget.style.borderColor = "#374151"; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = "#374151"; e.currentTarget.style.color = "#FFFFFF"; e.currentTarget.style.borderColor = "#FFFFFF"; }}
        onClick={() => setShowDeleteChoices(false)}>
        GO BACK NOW
      </button>
    </div>
  );
};

const AppointmentModal = ({
  closeAppointmentModal,
  draft,
  setDraftField,
  banner,
  customers,
  customerMobile,
  customerEmail,
  employees,
  jobTypes,
  useCustomerAddress,
  setUseCustomerAddress,
  applyCustomerAddressNow,
  showQuickAddCustomerUI,
  setShowQuickAddCustomerUI,
  quickCustomer,
  setQC,
  saveQuickCustomer,
  loadCustomers,
  saveAppointment,
  deleteAppointment,
  deleteRecurringAppointment,
  deleteRecurringAppointments,
  recurringDeletionPending,
  textOnMyWayHref,
  emailOnMyWayHref,
  directionsHref,
  onPrint,
  onViewFullSchedule,
  styles,
}) => {
  const [showDeleteChoices, setShowDeleteChoices] = useState(false);

  return (
  <div style={styles.modalOverlay} onMouseDown={closeAppointmentModal}>
    <div style={styles.modalCard} onMouseDown={(e) => e.stopPropagation()}>
      <div style={styles.modalBody}>
        <style>{`
          .jf-recurring-delete-mobile-label {
            display: none;
          }
          @media (max-width: 1023px) {
            .jf-appointment-final-actions {
              flex-direction: column !important;
              align-items: stretch !important;
              gap: 10px !important;
            }
            .jf-calendar-mobile-action {
              width: 100% !important;
              max-width: none !important;
              min-width: 0 !important;
              min-height: 48px !important;
              margin-left: 0 !important;
              box-sizing: border-box !important;
              font-size: 16px !important;
              font-weight: 900 !important;
            }
            .jf-calendar-mobile-delete {
              border-color: #000000 !important;
            }
            .jf-recurring-delete-desktop-label {
              display: none;
            }
            .jf-recurring-delete-mobile-label {
              display: inline;
            }
          }
        `}</style>
        <div style={{ fontWeight: 900, fontSize: 20, textTransform: "uppercase" }}>
          {draft.id ? "Edit Appointment" : "New Appointment"}
        </div>

        {banner ? (
          <div style={{ marginTop: 10, border: "3px solid #E8E2DA", borderRadius: 12, padding: 10, fontWeight: 900, background: "#ff5252", color: "white" }}>
            {banner}
          </div>
        ) : null}

        <div style={styles.section}>
          <div style={styles.sectionTitle}>Schedule</div>
          <div style={styles.row2}>
            <div>
              <div style={{ fontWeight: 900, marginBottom: 6 }}>START DATE</div>
              <input style={styles.input} type="date" value={draft.date} onChange={(e) => setDraftField("date", e.target.value)} />
            </div>
            <div>
              <div style={{ fontWeight: 900, marginBottom: 6 }}>END DATE <span style={{ fontWeight: 800, opacity: 0.7 }}>(leave blank for single-day)</span></div>
              <input style={styles.input} type="date" value={draft.end_date || ""} onChange={(e) => setDraftField("end_date", e.target.value)} />
            </div>
          </div>
          {draft.end_date && draft.end_date < draft.date ? (
            <div style={{ marginTop: 8, border: "3px solid #E8E2DA", borderRadius: 10, padding: "8px 12px", background: "#ff5252", fontWeight: 900 }}>
              ⚠️ End date is before start date.
            </div>
          ) : null}
          <div style={{ height: 10 }} />
          <div style={styles.row2}>
            <div>
              <div style={{ fontWeight: 900, marginBottom: 6 }}>START TIME</div>
              <TimeDropdown value={draft.start_time} onChange={(v) => setDraftField("start_time", v)} />
            </div>
            <div>
              <div style={{ fontWeight: 900, marginBottom: 6 }}>END TIME</div>
              <TimeDropdown value={draft.end_time} onChange={(v) => setDraftField("end_time", v)} />
            </div>
          </div>
        </div>

        <div style={styles.section}>
          <div style={styles.sectionTitle}>Customer</div>
          <div style={{ fontWeight: 900, marginBottom: 6 }}>CUSTOMER</div>
          <select style={styles.select} value={draft.customer_id} onChange={(e) => setDraftField("customer_id", e.target.value)}>
            <option value="">-- SELECT CUSTOMER --</option>
            {(customers || []).map((c) => (
              <option key={c.id} value={String(c.id)}>
                {(c.name || c.contactPerson || "").toString().trim() || "(no name)"}
              </option>
            ))}
          </select>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 10 }}>
            <button type="button" className="jf-calendar-mobile-action" style={styles.btn(false)}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#FFC107")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "white")}
              onClick={() => setShowQuickAddCustomerUI((v) => !v)}>
              ➕ QUICK ADD
            </button>
            <button type="button" className="jf-calendar-mobile-action" style={styles.btn(false)}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#FFC107")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "white")}
              onClick={loadCustomers}>
              🔄 REFRESH LIST
            </button>
          </div>

          {showQuickAddCustomerUI ? (
            <div style={{ marginTop: 12, padding: 12, border: "3px solid #E8E2DA", borderRadius: 14, background: "white", color: "black" }}>
                  <div style={styles.row2}>
                    <input style={styles.input} placeholder="CUSTOMER NAME / COMPANY" value={quickCustomer.company} onChange={(e) => setQC("company", e.target.value)} />
                    <input style={styles.input} placeholder="CONTACT (OPTIONAL)" value={quickCustomer.contact} onChange={(e) => setQC("contact", e.target.value)} />
                  </div>
                  <div style={{ height: 10 }} />
                  <div style={styles.row2}>
                    <input style={styles.input} placeholder="EMAIL" value={quickCustomer.email} onChange={(e) => setQC("email", e.target.value)} />
                    <input style={styles.input} placeholder="OFFICE PHONE" value={quickCustomer.office_phone} onChange={(e) => setQC("office_phone", e.target.value)} inputMode="tel" />
                  </div>
                  <div style={{ height: 10 }} />
                  <div style={styles.row2}>
                    <input style={styles.input} placeholder="MOBILE PHONE" value={quickCustomer.mobile_phone} onChange={(e) => setQC("mobile_phone", e.target.value)} inputMode="tel" />
                    <div style={{ width: "100%" }}>
                      <div style={{ fontWeight: 900, marginBottom: 6 }}>TERMS</div>
                      <select style={styles.select} value={quickCustomer.terms} onChange={(e) => setQC("terms", e.target.value)}>
                        {(TERMS_OPTIONS || []).map((t) => (<option key={t} value={t}>{t}</option>))}
                      </select>
                    </div>
                  </div>
                  <div style={{ height: 10 }} />
                  <div style={styles.row4}>
                    <AddressAutocomplete
                      inputStyle={styles.input}
                      placeholder="STREET"
                      value={quickCustomer.street}
                      onChange={(v) => setQC("street", v)}
                      onAddressSelect={({ street, city, state, zip }) => {
                        setQC("street", street);
                        setQC("city", city);
                        setQC("state", state.slice(0, 2));
                        setQC("zip", zip);
                      }}
                    />
                    <input style={styles.input} placeholder="CITY" value={quickCustomer.city} onChange={(e) => setQC("city", e.target.value)} />
                    <input style={styles.input} placeholder="STATE" value={quickCustomer.state} onChange={(e) => setQC("state", e.target.value)} />
                    <input style={styles.input} placeholder="ZIP" value={quickCustomer.zip} onChange={(e) => setQC("zip", e.target.value)} />
                  </div>
                  <div style={{ height: 10 }} />
                  <textarea style={styles.textarea} placeholder="NOTES" value={quickCustomer.notes} onChange={(e) => setQC("notes", e.target.value)} />
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 12, justifyContent: "space-between" }}>
                    <button type="button" className="jf-calendar-mobile-action" style={styles.btn(false)}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "#FFC107")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "white")}
                      onClick={saveQuickCustomer}>✅ SAVE CUSTOMER</button>
                    <button type="button" className="jf-calendar-mobile-action" style={styles.btn(false)}
                      onMouseEnter={(e) => (e.currentTarget.style.background = "#ff5252")}
                      onMouseLeave={(e) => (e.currentTarget.style.background = "white")}
                      onClick={() => setShowQuickAddCustomerUI(false)}>CANCEL</button>
                  </div>
            </div>
          ) : null}

          {customerMobile ? (
            <div style={{ ...styles.contactRow, flexDirection: "column", alignItems: "stretch" }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 900, fontSize: 12 }}>PHONE</div>
                <div style={{ fontWeight: 800, overflowWrap: "anywhere" }}>{customerMobile}</div>
              </div>
              {textOnMyWayHref ? (
                <button type="button" className="jf-calendar-mobile-action" style={{ ...styles.compactBtn, ...styles.textBtn, width: "100%", height: 44 }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "#FFC107"; e.currentTarget.style.color = "#000000"; e.currentTarget.style.borderColor = "#FFA500"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "#008000"; e.currentTarget.style.color = "#FFFFFF"; e.currentTarget.style.borderColor = "#000000"; }}
                  onClick={() => { window.location.href = textOnMyWayHref; }}>
                  TEXT — ON MY WAY
                </button>
              ) : null}
            </div>
          ) : null}

          {customerEmail ? (
            <div style={{ ...styles.contactRow, flexDirection: "column", alignItems: "stretch" }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 900, fontSize: 12 }}>EMAIL</div>
                <div style={{ fontWeight: 800, overflowWrap: "anywhere" }}>{customerEmail}</div>
              </div>
              {emailOnMyWayHref ? (
                <button type="button" className="jf-calendar-mobile-action" style={{ ...styles.compactBtn, ...styles.emailBtn, width: "100%", height: 44 }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "#FFC107"; e.currentTarget.style.color = "#000000"; e.currentTarget.style.borderColor = "#FFA500"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "#0000FF"; e.currentTarget.style.color = "#FFFFFF"; e.currentTarget.style.borderColor = "#000000"; }}
                  onClick={() => { window.location.href = emailOnMyWayHref; }}>
                  EMAIL — ON MY WAY
                </button>
              ) : null}
            </div>
          ) : null}
        </div>

        <div style={styles.section}>
          <div style={styles.sectionTitle}>Job Details / Assignment</div>
          <div style={styles.row2}>
            <div>
              <div style={{ fontWeight: 900, marginBottom: 6 }}>EMPLOYEE</div>
              <select style={styles.select} value={draft.employee_id} onChange={(e) => setDraftField("employee_id", e.target.value)}>
                <option value="">-- SELECT EMPLOYEE --</option>
                {(employees || []).map((e) => (
                  <option key={e.id} value={String(e.id)}>{String(e.name || "").trim() || "(no name)"}</option>
                ))}
              </select>
              <div style={{ height: 10 }} />
              <div style={{ fontWeight: 900, marginBottom: 6 }}>JOB TYPE</div>
              <select style={styles.select} value={draft.job_type_id} onChange={(e) => setDraftField("job_type_id", e.target.value)}>
                <option value="">-- SELECT JOB TYPE --</option>
                {(jobTypes || []).map((jt) => (
                  <option key={jt.id} value={String(jt.id)}>{String(jt.name || jt.title || "").trim() || "(no name)"}</option>
                ))}
              </select>
              <div style={{ height: 10 }} />
              <label style={{ display: "flex", gap: 10, alignItems: "center", fontWeight: 900 }}>
                <input type="checkbox" checked={!!draft.estimate_on_file} onChange={(e) => setDraftField("estimate_on_file", e.target.checked)} style={{ width: 20, height: 20 }} />
                ESTIMATE ON FILE
              </label>
            </div>
            <div>
              <div style={{ fontWeight: 900, marginBottom: 6 }}>DETAILS / NOTES</div>
              <textarea style={styles.textarea} placeholder="DETAILS OF JOB / LARGE NOTES" value={draft.details} onChange={(e) => setDraftField("details", e.target.value)} />
            </div>
          </div>
          {draft.id && typeof onPrint === "function" ? (
            <div style={{ marginTop: 10 }}>
              <button type="button" className="jf-calendar-mobile-action" style={{ ...styles.compactBtn, ...styles.printBtn, width: "100%", height: 44 }} disabled={recurringDeletionPending}
                onMouseEnter={(e) => { e.currentTarget.style.background = "#FFC107"; e.currentTarget.style.color = "#000000"; e.currentTarget.style.borderColor = "#FFA500"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "#000000"; e.currentTarget.style.color = "#E8C98F"; e.currentTarget.style.borderColor = "#E8761D"; }}
                onClick={onPrint}>PRINT WORK ORDER</button>
            </div>
          ) : null}
        </div>

        <div style={styles.section}>
          <div style={styles.sectionTitle}>Service Address</div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
            <input type="checkbox" checked={useCustomerAddress}
              onChange={(e) => {
                const checked = e.target.checked;
                setUseCustomerAddress(checked);
                if (checked) applyCustomerAddressNow();
              }}
              style={{ width: 20, height: 20 }} disabled={!draft.customer_id} />
            <div style={{ fontWeight: 900 }}>
              SERVICE ADDRESS SAME AS CUSTOMER ADDRESS
              {!draft.customer_id ? <span style={{ fontWeight: 800, opacity: 0.7 }}> (select customer first)</span> : null}
            </div>
          </div>
          <div style={styles.row4}>
            <AddressAutocomplete
              inputStyle={styles.input}
              placeholder="STREET"
              value={draft.service_street}
              onChange={(v) => setDraftField("service_street", v)}
              onAddressSelect={({ street, city, state, zip }) => {
                setDraftField("service_street", street);
                setDraftField("service_city", city);
                setDraftField("service_state", state.slice(0, 2));
                setDraftField("service_zip", zip);
              }}
            />
            <input style={styles.input} placeholder="CITY" value={draft.service_city} onChange={(e) => setDraftField("service_city", e.target.value)} />
            <input style={styles.input} placeholder="ST" value={draft.service_state} onChange={(e) => setDraftField("service_state", e.target.value)} />
            <input style={styles.input} placeholder="ZIP" value={draft.service_zip} onChange={(e) => setDraftField("service_zip", e.target.value)} />
          </div>
          {directionsHref ? (
            <div style={{
              display: "flex",
              justifyContent: "center",
              width: "100%",
              margin: "14px 0 4px",
            }}>
              <button type="button" className="jf-calendar-mobile-action" style={{ ...styles.compactBtn, ...styles.directionsBtn, width: "100%", height: 44 }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "#FFC107"; e.currentTarget.style.color = "#000000"; e.currentTarget.style.borderColor = "#FFA500"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "#E8761D"; e.currentTarget.style.color = "#000000"; e.currentTarget.style.borderColor = "#000000"; }}
                onClick={() => { window.location.href = directionsHref; }}>
                DIRECTIONS NOW
              </button>
            </div>
          ) : null}
        </div>

        <div className="jf-appointment-final-actions" style={{ ...styles.finalActions, flexDirection: "column", alignItems: "stretch" }}>
          {!showDeleteChoices ? (
            <>
          <button type="button" className="jf-calendar-mobile-action" style={{ ...styles.saveBtn, width: "100%", boxSizing: "border-box" }} disabled={recurringDeletionPending}
            onMouseEnter={(e) => { e.currentTarget.style.background = "#FFC107"; e.currentTarget.style.color = "#000000"; e.currentTarget.style.borderColor = "#FFA500"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "#000000"; e.currentTarget.style.color = "#FFA500"; e.currentTarget.style.borderColor = "#E8761D"; }}
            onClick={saveAppointment}>SAVE NOW</button>

          {draft.id && draft.recurrence_batch_id != null && draft.recurrence_sequence != null && typeof onViewFullSchedule === "function" ? (
            <button type="button" className="jf-calendar-mobile-action" style={{ ...styles.compactBtn, width: "100%", minHeight: 48, background: "#3a2000", color: "#E8E2DA", border: "2px solid #E8E2DA" }} disabled={recurringDeletionPending}
              onMouseEnter={(e) => { e.currentTarget.style.background = "#E8E2DA"; e.currentTarget.style.color = "#3a2000"; e.currentTarget.style.borderColor = "#3a2000"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "#3a2000"; e.currentTarget.style.color = "#E8E2DA"; e.currentTarget.style.borderColor = "#E8E2DA"; }}
              onClick={onViewFullSchedule}>VIEW SCHEDULE NOW</button>
          ) : null}

          <button type="button" className="jf-calendar-mobile-action" style={{ ...styles.cancelBtn, width: "100%", boxSizing: "border-box" }} disabled={recurringDeletionPending}
            onMouseEnter={(e) => { e.currentTarget.style.background = "#FFC107"; e.currentTarget.style.color = "#000000"; e.currentTarget.style.borderColor = "#FFA500"; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = "#FFFFFF"; e.currentTarget.style.color = "#000000"; e.currentTarget.style.borderColor = "#000000"; }}
            onClick={closeAppointmentModal}>{draft.id ? 'CLOSE AND GO TO CALENDAR' : 'CANCEL'}</button>
            </>
          ) : null}

          {draft.id ? (
            <AppointmentDeleteAction
              draft={draft}
              deleteAppointment={deleteAppointment}
              deleteRecurringAppointment={deleteRecurringAppointment}
              deleteRecurringAppointments={deleteRecurringAppointments}
              recurringDeletionPending={recurringDeletionPending}
              showDeleteChoices={showDeleteChoices}
              setShowDeleteChoices={setShowDeleteChoices}
              styles={styles}
            />
          ) : null}
          {draft.id && !showDeleteChoices ? (
            <AppointmentShareControls
              appointmentId={draft.id}
              customerMobile={customerMobile}
              customerEmail={customerEmail}
            />
          ) : null}
        </div>
      </div>
    </div>
  </div>
  );
};

export default AppointmentModal;
